/**
 * Species Utilities (Frontend)
 *
 * This module re-exports species utilities from shared/domain/petHelper
 * and provides frontend-specific utilities like slug generation.
 *
 * Most functions are now in shared/domain/petHelper for use by both frontend and backend.
 *
 * @module utils/species
 */

import type { PetWithStatus, SuikamonSpecies } from '@shared/domain/petModels';
import { SUIKAMON_SPECIES } from '@shared/domain/speciesConfig';
import {
  getSpeciesById,
  getSpeciesByName,
  getAvailableSpecies,
  isValidSpeciesId,
  getMaxStage,
  getStageLevelRequirement,
  getSpeciesXpRange,
} from '@shared/domain/petHelper';

// Re-export all shared species utilities
export {
  getSpeciesById,
  getSpeciesByName,
  getAvailableSpecies,
  isValidSpeciesId,
  getMaxStage,
  getStageLevelRequirement,
  getSpeciesXpRange,
};

export interface SpeciesGroup {
  species: SuikamonSpecies;
  pets: PetWithStatus[];
}

/**
 * Groups pets by species, sorted with owned species first.
 *
 * @param pets - Array of pets to group
 * @param includeUnowned - If true, includes all available species even if unowned (PlayPage).
 *                         If false, only includes species the user actually owns (TrainerProfile).
 */
export function groupPetsBySpecies(
  pets: PetWithStatus[],
  includeUnowned: boolean,
): SpeciesGroup[] {
  const petMap = new Map<number, PetWithStatus[]>();
  for (const pet of pets) {
    const speciesId = pet.status.species;
    if (!petMap.has(speciesId)) {
      petMap.set(speciesId, []);
    }
    petMap.get(speciesId)!.push(pet);
  }

  return SUIKAMON_SPECIES
    .filter(s => s.available && (includeUnowned || petMap.has(s.id)))
    .map(species => ({ species, pets: petMap.get(species.id) || [] }))
    .sort((a, b) => {
      if (a.pets.length > 0 && b.pets.length === 0) return -1;
      if (a.pets.length === 0 && b.pets.length > 0) return 1;
      if (b.pets.length !== a.pets.length) return b.pets.length - a.pets.length;
      return a.species.name.localeCompare(b.species.name);
    });
}

/**
 * Generates a URL-safe slug from a species ID.
 *
 * Process:
 * 1. Look up species by ID
 * 2. Normalize species name (remove diacritics)
 * 3. Convert to lowercase kebab-case
 * 4. Fallback to "species{id}" if name is empty
 *
 * Used for:
 * - Image URLs (e.g., /species/bireon-evolution1.png)
 * - Background images (e.g., /img/bireon-background.png)
 * - Route parameters
 *
 * @param speciesId - The species ID
 * @returns URL-safe slug (e.g., "bireon", "terrapin")
 *
 * @example
 * getSpeciesSlug(1) // "bireon"
 * getSpeciesSlug(2) // "terrapin"
 * getSpeciesSlug(999) // "species999" (fallback for unknown species)
 */
export function getSpeciesSlug(speciesId: number): string {
  // Use the already-imported getSpeciesById function
  const species = getSpeciesById(speciesId);
  const speciesName = species?.name?.trim();

  // Use species name if available, otherwise fallback to generic pattern
  const slugSource = speciesName && speciesName.length > 0 ? speciesName : `species${speciesId}`;

  // Normalize to remove diacritics (é → e, ñ → n, etc.)
  const normalizedSource = slugSource.normalize?.('NFKD') ?? slugSource;

  // Convert to kebab-case slug
  const speciesSlug = normalizedSource
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .replace(/[^a-z0-9]+/gi, '-')    // Replace non-alphanumeric with hyphens
    .toLowerCase()
    .replace(/^-+|-+$/g, '');        // Trim leading/trailing hyphens

  // Ensure we always return a valid slug
  return speciesSlug.length > 0 ? speciesSlug : `species${speciesId}`;
}
